import React, { useState } from 'react'

import {
    NoticeBox,
    NoticeBoxColors,
    NoticeBoxVariants,
    Spinner,
    TextLink,
    VsExclamationIcon,
} from '@octane/spark'
import { useLender } from 'contexts/LenderContextProvider'
import useLenderDisclosure from 'hooks/useLenderDisclosure'
import DisclosureType from 'state/enums/DisclosureType'

import {
    DisclosureContainer,
    DisclosureParagraph,
    DisclosureSpinnerWrapper,
    DisclosureTitle,
    FinePrint,
    FooterText,
    RuleLine,
} from './DecisionResult.styled'

interface ApprovalDisclosureProps {
    readonly applicationId?: number
}

interface ApprovalDisclosureContentProps {
    readonly applicationId?: number
    readonly lenderId?: number
}

function ApprovalDisclosureContent({
    applicationId,
    lenderId,
}: ApprovalDisclosureContentProps): JSX.Element {
    const {
        disclosureText,
        error: disclosureError,
        loading: disclosureLoading,
    } = useLenderDisclosure(
        DisclosureType.CREDIT_SOFT_PULL_APPLICATION_APPROVAL,
        lenderId,
        applicationId,
    )

    if (disclosureError) {
        return (
            <DisclosureContainer>
                <NoticeBox
                    title=""
                    icon={VsExclamationIcon}
                    color={NoticeBoxColors.WARNING}
                    variant={NoticeBoxVariants.BOX_HIGHLIGHTED}
                >
                    An error occurred when loading the disclosure. Refresh the
                    page to try again or contact support.
                </NoticeBox>
            </DisclosureContainer>
        )
    }

    if (disclosureLoading || !disclosureText) {
        return (
            <DisclosureSpinnerWrapper data-testid="DecisionResultApproved-Disclosure-spinner">
                <Spinner size={24} />
            </DisclosureSpinnerWrapper>
        )
    }

    return (
        <DisclosureContainer>
            <RuleLine />
            <DisclosureTitle>Soft Credit Pull Disclosure</DisclosureTitle>
            <div>
                {disclosureText.split('\n').map((paragraph) => (
                    <DisclosureParagraph key={paragraph}>
                        {paragraph}
                    </DisclosureParagraph>
                ))}
            </div>
        </DisclosureContainer>
    )
}

export default function ApprovalDisclosure({
    applicationId,
}: ApprovalDisclosureProps): JSX.Element {
    const [showDisclosure, setShowDisclosure] = useState(false)
    const { id: lenderId } = useLender()

    return (
        <>
            <FinePrint>
                <FooterText>
                    Applicants are subject to a full credit approval.{' '}
                </FooterText>
                <TextLink
                    onClick={() => setShowDisclosure(true)}
                    data-testid="DecisionResultApproved-read-disclosure-link"
                >
                    Read Disclosure
                </TextLink>
            </FinePrint>
            <div>
                {showDisclosure && (
                    <ApprovalDisclosureContent
                        applicationId={applicationId}
                        lenderId={lenderId}
                    />
                )}
            </div>
        </>
    )
}
